import React from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, Clock, Video, RefreshCw, ArrowRight } from "lucide-react";
import { Seo } from "../lib/seo";
import { Section, Button } from "../components/ui";

// TidyCal passes booking details as query params after a confirmed booking.
// Configure the TidyCal redirect URL to:
//   https://connect.bybloomlabs.com/see-you?name={{contact.name}}&email={{contact.email}}
//   &date={{booking.date}}&time={{booking.time}}&starts_at={{booking.starts_at}}
//   &timezone={{booking.timezone}}&type={{booking_type.title}}
//   &meeting_url={{booking.meeting_url}}&reschedule_url={{booking.reschedule_url}}
export default function SeeYou() {
  const [params] = useSearchParams();
  const g = (...keys) => {
    for (const k of keys) { const v = params.get(k); if (v && v.trim() && !v.includes("{{")) return v.trim(); }
    return "";
  };
  const name = g("name", "contact.name");
  const date = g("date", "booking.date");
  const time = g("time", "booking.time", "starts_at", "booking.starts_at");
  const timezone = g("timezone", "booking.timezone");
  const type = g("type", "title", "booking_type.title") || "Intro call";
  const meetingUrl = g("meeting_url", "booking.meeting_url");
  const rescheduleUrl = g("reschedule_url", "booking.reschedule_url");
  const firstName = name ? name.split(" ")[0] : "";

  return (
    <>
      <Seo title="You're booked" path="/see-you/" description="Your BloomCONNECT intro call is confirmed. We'll see you soon." noindex />
      <Section>
        <div className="max-w-xl mx-auto text-center" data-testid="see-you-page">
          <div className="mx-auto h-16 w-16 rounded-full bg-brand-700 text-white grid place-items-center">
            <CalendarCheck className="h-8 w-8" />
          </div>
          <h1 className="mt-6 text-4xl md:text-5xl tracking-tight text-ink-950" data-testid="see-you-title">
            {firstName ? `See you soon, ${firstName}!` : "You're booked — see you soon!"}
          </h1>
          <p className="mt-4 text-lg text-ink-600">
            Your <b className="text-ink-900">{type}</b> with BloomCONNECT is confirmed. We've sent the details to your email — a calendar invite should be on its way.
          </p>

          {(date || time) && (
            <div className="mt-8 rounded-xl border border-line bg-surface-sunk p-6 text-left space-y-3" data-testid="see-you-details">
              {date && (
                <div className="flex items-center gap-3">
                  <CalendarCheck className="h-5 w-5 text-brand-700 shrink-0" />
                  <span className="text-ink-900" data-testid="see-you-date">{date}</span>
                </div>
              )}
              {time && (
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-brand-700 shrink-0" />
                  <span className="text-ink-900" data-testid="see-you-time">{time}{timezone ? ` · ${timezone}` : ""}</span>
                </div>
              )}
              {meetingUrl && (
                <div className="flex items-center gap-3">
                  <Video className="h-5 w-5 text-brand-700 shrink-0" />
                  <a href={meetingUrl} target="_blank" rel="noreferrer" className="text-brand-700 underline break-all" data-testid="see-you-meeting">Join the meeting</a>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {meetingUrl && (
              <Button href={meetingUrl} target="_blank" rel="noreferrer" variant="primary" size="lg" data-testid="see-you-join-btn">
                Join meeting <ArrowRight className="h-4 w-4 ml-1.5 inline" />
              </Button>
            )}
            {rescheduleUrl && (
              <Button href={rescheduleUrl} target="_blank" rel="noreferrer" variant="secondary" size="lg" data-testid="see-you-reschedule-btn">
                <RefreshCw className="h-4 w-4 mr-1.5 inline" /> Reschedule
              </Button>
            )}
            <Button to="/" variant="secondary" size="lg" data-testid="see-you-home-btn">Back to home</Button>
          </div>

          <p className="mt-8 text-sm text-ink-500">
            Want to explore before we talk? <a href="/pricing/" className="text-brand-700 underline">See pricing</a> or <a href="/features/" className="text-brand-700 underline">tour the features</a>.
          </p>
        </div>
      </Section>
    </>
  );
}
